import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import { NotificationMessage } from "../shared/models/models";
import { Login } from "./models/AccountModels";
import { SerializedError } from "../shared/models/errorHandlingModels";
import { login } from "./api/accountApi";
import accountContext from "./accountContext";
import { serializeError, createErrorNotification } from "../shared/utils/errorUtils";

interface LoginState {
  isLoading: boolean;
  formNotificationMessage?: NotificationMessage;
  showPassword: boolean;
}

const initialState: LoginState = {
  isLoading: false,
  formNotificationMessage: undefined,
  showPassword: false,
};

export const handleLogin = createAsyncThunk(
  "login/handleLogin",
  async (model: Login, { rejectWithValue }) => {
    try {
      const loginResult = await login(model);
      if (!loginResult) {
        return rejectWithValue(serializeError({ message: "Login failed" }));
      }

      accountContext.finishLogin(loginResult);
      return { success: true };
    } catch (error: any) {
      return rejectWithValue(serializeError(error));
    }
  }
);

const loginSlice = createSlice({
  name: "login",
  initialState,
  reducers: {
    togglePasswordVisibility: (state) => {
      state.showPassword = !state.showPassword;
    },
    resetLoginState: (state) => {
      state.isLoading = false;
      state.formNotificationMessage = undefined;
      state.showPassword = false;
    },
    setFormNotificationMessage: (state, action: PayloadAction<NotificationMessage | undefined>) => {
      state.formNotificationMessage = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(handleLogin.pending, (state) => {
        state.isLoading = true;
        state.formNotificationMessage = undefined;
      })
      .addCase(handleLogin.fulfilled, (state) => {
        state.isLoading = false;
        state.formNotificationMessage = undefined;
      })
      .addCase(handleLogin.rejected, (state, action) => {
        state.isLoading = false;
        state.formNotificationMessage = createErrorNotification(action.payload as SerializedError);
      });
  },
});

export const {
  togglePasswordVisibility,
  resetLoginState,
  setFormNotificationMessage,
} = loginSlice.actions;

export default loginSlice.reducer;
